import { z } from 'zod';
import { ActivationId } from './activation.ts';
import { Id, IsoDateTime, JsonObject, Sha256Hex, TenantId } from './common.ts';
import { EventTrigger } from './process.ts';

/**
 * 17a (ADR-018): inbound external events. Authenticate and validate, persist in a durable inbox, acknowledge
 * only after the write commits, then resolve subscriptions and deliver through the existing event transport to
 * workflow signals. Every received event ends with exactly one `InboundDisposition` per delivery pass.
 */

/** Business correlation carried by (or derived from) the event. */
export const InboundCorrelation = z
  .object({
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    activationId: ActivationId.optional(),
    /** Pack business key, e.g. `po:PO-1001` or `payment:PAY-77`. */
    businessKey: z.string().min(1).max(255).optional(),
    /**
     * Several business keys on one event (e.g. a payment-batch status addressing several cases). A subscription
     * matching `businessKey` matches when the key equals `businessKey` or is listed here. Events only; a
     * subscription `match` never uses it.
     */
    businessKeys: z.array(z.string().min(1).max(255)).min(1).max(1000).optional(),
    /**
     * Added by 26a (brief 26 §4.4): the obligation this event answers. Delivery calls the `OBLIGATION_MATCHER` before
     * subscription matching (`delivered`, `duplicate` for the same event id, `late` for a settled obligation or an
     * abandoned case).
     */
    obligationId: Id.optional(),
  })
  .meta({ id: 'InboundCorrelation' });
export type InboundCorrelation = z.infer<typeof InboundCorrelation>;

/** How the source was authenticated. Unauthenticated events are refused before admission (never stored). */
export const InboundSignatureMeta = z
  .object({
    scheme: z.enum(['hmac-sha256', 'jws', 'mtls', 'bearer']),
    keyId: z.string().min(1).optional(),
    /** Source identity the credential belongs to; must equal `source` and own `tenantId`. */
    principal: z.string().min(1),
    verifiedAt: IsoDateTime,
  })
  .meta({ id: 'InboundSignatureMeta' });
export type InboundSignatureMeta = z.infer<typeof InboundSignatureMeta>;

/** An authenticated external event as stored in the inbox. Unique by `(tenantId, source, eventId)`. */
export const InboundEvent = z
  .object({
    /** Source-assigned id (dedupe key). */
    eventId: z.string().min(1).max(255),
    source: z.string().min(1).max(200),
    tenantId: TenantId,
    type: z.string().min(1).max(200),
    schemaVersion: z.string().min(1).max(50),
    correlation: InboundCorrelation.default({}),
    /** Source-side ordering (e.g. bank status sequence); later sequences never regress state. */
    sequence: z.number().int().nonnegative().optional(),
    occurredAt: IsoDateTime.optional(),
    receivedAt: IsoDateTime,
    payload: JsonObject,
    payloadHash: Sha256Hex,
    signature: InboundSignatureMeta,
  })
  .meta({ id: 'InboundEvent' });
export type InboundEvent = z.infer<typeof InboundEvent>;

export const InboundDisposition = z
  .enum(['delivered', 'triggered', 'duplicate', 'unmatched', 'late', 'poison', 'replayed'])
  .meta({ id: 'InboundDisposition' });
export type InboundDisposition = z.infer<typeof InboundDisposition>;

/** Inbox row: the event plus its delivery history. */
export const InboundRecord = z
  .object({
    inboxId: Id,
    event: InboundEvent,
    disposition: InboundDisposition.optional(),
    /** Delivery attempts (each pass after commit or replay). */
    attempts: z.number().int().nonnegative(),
    deliveredTo: z
      .array(z.object({ subscriptionId: Id, runId: Id, signal: z.string().min(1), at: IsoDateTime }))
      .default([]),
    lastError: z.object({ code: z.string(), message: z.string() }).optional(),
    /** For `triggered`: the start it caused (dedupe key from `triggerDedupeKey`). */
    triggered: z
      .object({
        processName: z.string().min(1),
        dedupeKey: z.string().min(1),
        processInstanceId: Id.optional(),
      })
      .optional(),
    /**
     * Added by 17c1 (review S3): every process start attempted for a `triggered` row (one per matching trigger), with
     * the error of a candidate whose start was refused permanently.
     */
    triggerStarts: z
      .array(
        z.object({
          processName: z.string().min(1),
          dedupeKey: z.string().min(1).optional(),
          processInstanceId: Id.optional(),
          created: z.boolean().optional(),
          error: z.object({ code: z.string(), message: z.string() }).optional(),
        }),
      )
      .optional(),
    /**
     * For `unmatched`: retained for early arrival until this time (a pack/profile policy); after it the row is
     * `late` and only an operator replay delivers it.
     */
    retentionUntil: IsoDateTime.optional(),
    /** Operator replay: who and why. */
    replays: z
      .array(z.object({ by: z.string().min(1), reason: z.string().min(1), at: IsoDateTime }))
      .default([]),
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'InboundRecord' });
export type InboundRecord = z.infer<typeof InboundRecord>;

/**
 * A run's interest in inbound events. Opened by an activity (correlated to that activation revision), closed
 * when the activity ends. Unmatched events that arrived earlier are delivered when a matching subscription opens
 * (early arrival is retained until `retentionUntil`), **in receipt order** (`receivedAt`, then inbox id).
 * Ordering state is owned by the inbox: `ordering.lastSequence` is advanced by the inbox in the same transaction
 * that records a delivery, never by the run or the sender.
 */
export const InboundSubscription = z
  .object({
    subscriptionId: Id,
    tenantId: TenantId,
    source: z.string().min(1),
    type: z.string().min(1),
    /** Every set field must equal the event correlation. At least one field is required. */
    match: InboundCorrelation.refine((m) => Object.values(m).some((v) => v !== undefined), {
      message: 'subscription match needs at least one correlation field',
    }).refine((m) => m.businessKeys === undefined, {
      message: 'a subscription matches one businessKey; businessKeys is for events',
    }),
    target: z.object({
      runId: Id,
      processInstanceId: Id.optional(),
      activationId: ActivationId.optional(),
      /** Signal name delivered to the run. */
      signal: z.string().min(1),
    }),
    ordering: z
      .discriminatedUnion('mode', [
        z.object({ mode: z.literal('none') }),
        z.object({ mode: z.literal('sequence'), lastSequence: z.number().int().nonnegative().optional() }),
      ])
      .default({ mode: 'none' }),
    state: z.enum(['active', 'closed']),
    createdAt: IsoDateTime,
    closedAt: IsoDateTime.optional(),
  })
  .meta({ id: 'InboundSubscription' });
export type InboundSubscription = z.infer<typeof InboundSubscription>;
export type InboundSubscriptionInput = z.input<typeof InboundSubscription>;

/** Event-triggered process start (same shape as the process trigger). */
export const InboundTrigger = EventTrigger;
export type InboundTrigger = EventTrigger;

/**
 * True when every set field of `match` equals the event's correlation; a `businessKey` also matches an entry of
 * the event's `businessKeys`.
 */
export function correlationMatches(match: InboundCorrelation, correlation: InboundCorrelation): boolean {
  if (match.caseId !== undefined && match.caseId !== correlation.caseId) return false;
  if (match.processInstanceId !== undefined && match.processInstanceId !== correlation.processInstanceId) {
    return false;
  }
  if (match.activationId !== undefined && match.activationId !== correlation.activationId) return false;
  if (match.businessKey !== undefined) {
    const keys = [
      ...(correlation.businessKey === undefined ? [] : [correlation.businessKey]),
      ...(correlation.businessKeys ?? []),
    ];
    if (!keys.includes(match.businessKey)) return false;
  }
  return true;
}

export interface InboundClassificationInput {
  event: Pick<InboundEvent, 'tenantId' | 'source' | 'type' | 'correlation' | 'sequence' | 'payloadHash'>;
  /** The data schema for (type, schemaVersion) accepted the payload. */
  schemaValid: boolean;
  /** Payload hash of an earlier inbox row with the same `(tenantId, source, eventId)`, if any. */
  previousPayloadHash?: string;
  /** True when an operator replays a stored event. */
  replay?: boolean;
  /** An `EventTrigger` of a registered process matches `(source, type)` (and its business key is present). */
  triggerMatched?: boolean;
  /** Subscriptions of the same tenant, source and type. */
  subscriptions: readonly Pick<
    InboundSubscription,
    'tenantId' | 'source' | 'type' | 'match' | 'ordering' | 'state'
  >[];
}

/**
 * Pure disposition rule (ADR-018), applied after authentication, in this order:
 * - same event id and payload already received → `duplicate` (acknowledged, not redelivered);
 *   same id with another payload or an invalid payload → `poison` (parked for an operator);
 * - active matching subscriptions → `delivered` (to each of them; `replayed` for an operator replay), unless
 *   sequence ordering says the event is stale for all of them → `late`;
 * - no active subscription but a matching `EventTrigger` → `triggered` (one start per `triggerDedupeKey`);
 * - only closed matching subscriptions → `late` (the activity ended; state never regresses);
 * - nothing matches → `unmatched` (retained until `retentionUntil`; delivered when a matching subscription opens).
 * Subscriptions of another tenant never match (forged cross-tenant correlation cannot deliver).
 */
export function classifyInbound(input: InboundClassificationInput): InboundDisposition {
  const { event } = input;
  if (input.previousPayloadHash !== undefined && !input.replay) {
    return input.previousPayloadHash === event.payloadHash ? 'duplicate' : 'poison';
  }
  if (!input.schemaValid) return 'poison';
  const matching = input.subscriptions.filter(
    (s) =>
      s.tenantId === event.tenantId &&
      s.source === event.source &&
      s.type === event.type &&
      correlationMatches(s.match, event.correlation),
  );
  const active = matching.filter((s) => s.state === 'active');
  if (active.length === 0) {
    if (input.triggerMatched) return 'triggered';
    return matching.length > 0 ? 'late' : 'unmatched';
  }
  const fresh = active.filter(
    (s) =>
      s.ordering.mode === 'none' ||
      event.sequence === undefined ||
      s.ordering.lastSequence === undefined ||
      event.sequence > s.ordering.lastSequence,
  );
  if (fresh.length === 0) return 'late';
  return input.replay ? 'replayed' : 'delivered';
}

export const INBOUND_EVENT_TYPES = {
  received: 'inbound.event.received',
  disposed: 'inbound.event.disposed',
} as const;

/** `inbound.event.received` / `inbound.event.disposed` (ids and correlation only, never the payload). */
export const InboundEventNoticeData = z
  .object({
    inboxId: Id,
    eventId: z.string(),
    source: z.string(),
    type: z.string(),
    correlation: InboundCorrelation,
    disposition: InboundDisposition.optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'InboundEventNoticeData' });
export type InboundEventNoticeData = z.infer<typeof InboundEventNoticeData>;
